// The NEO2 platform navigation, defined once.
//
// Five apps render a left rail that has to look like ONE navigation, because to
// the person clicking it, it is one: nano-fibre-dashboard's Sidebar.jsx and the
// PlatformSidebar.jsx in nano-fibre-tech, -logistics, -training and
// -inspections. They cannot import from each other — CRA's ModuleScopePlugin
// forbids reaching outside src/, and three of the five live in separate
// repositories — so this file is COPIED into all five and kept byte-identical.
//
// Edit the copy in nano-fibre-dashboard. Then, from the platform repo root:
//     tools/nav-manifest.sh sync     # write this file into the other four
//     tools/nav-manifest.sh check    # exit 1 if any copy has drifted
//
// What lives here is the DATA: which entries exist, their labels, icons, order,
// and the paths they point at. Every drift found so far has been data — a
// missing Directory link, an absent Platform > Integrations entry, placeholders
// in one rail but not another — and never markup. So rendering stays each app's
// own job: the dashboard *selects* a department in-app while the other four
// *deep-link* to it, and only the dashboard has per-department cards. Sharing
// the markup too would mean a published package and a release cycle to change a
// label; sharing the data costs one script.
//
// Icons are imported here rather than named as strings, because a string would
// need a name -> component map in each app, which is one more thing to keep in
// step — exactly what this file exists to stop.
import {
  AcademicCapIcon,
  ArchiveBoxIcon,
  CpuChipIcon,
  IdentificationIcon,
  CubeIcon,
  DevicePhoneMobileIcon,
  InboxIcon,
  LifebuoyIcon,
  LockClosedIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  SignalIcon,
  TruckIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

// Non-clickable "coming soon" entries, rendered greyed-out. They sit at the top
// of every rail so the shape of the finished nav is visible before the features
// behind them exist.
export const PLACEHOLDER_ITEMS = [];

// Shown to every signed-in user regardless of department access.
//
// My Learning applies company-wide (HR induction, toolbox talks), not just to
// Training department members, so it deliberately sits outside the department
// gating below. Course-taking is ungated server-side to match.
//
// Dropped when REACT_APP_TRAINING_URL is unset, which is how a module absent
// from an environment disappears instead of linking somewhere that isn't there.
// Read as a literal member expression because CRA's DefinePlugin substitutes
// process.env.REACT_APP_* textually — process.env[someVariable] is NOT inlined
// and would silently evaluate to undefined in every build.
export const ALWAYS_VISIBLE_LINKS = [
  {
    name: 'My Learning',
    icon: AcademicCapIcon,
    url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/my-learning` : null,
  },
].filter((item) => item.url);

// Company-wide too, but a route the dashboard owns rather than another module's
// URL — hence `path`. The dashboard renders it directly; the other four prefix
// it with REACT_APP_DASHBOARD_URL.
export const ALWAYS_VISIBLE_ROUTES = [
  { name: 'Directory', icon: UsersIcon, path: '/directory' },
  { name: 'Inbox', icon: InboxIcon, path: '/messages' },
  // Anybody in the company can raise a ticket, so this is ungated like the
  // other two. It was the greyed-out 'Helpdesk' placeholder until the
  // feature behind it existed.
  { name: 'Helpdesk', icon: LifebuoyIcon, path: '/helpdesk' },
  // Build your Outlook email signature — everyone has one.
  { name: 'Email Signature', icon: PencilSquareIcon, path: '/signatures' },
];

// Platform is NOT a department — it is the platform's own internals (which
// external systems we talk to, when they last ran, what they picked up), shown
// to full admins only and deliberately outside the department model. Also
// dashboard-owned routes.
export const PLATFORM_ITEMS = [
  { name: 'Integrations', icon: SignalIcon, path: '/platform/integrations' },
  { name: 'Resources', icon: CpuChipIcon, path: '/platform/resources' },
  { name: 'Backups', icon: ArchiveBoxIcon, path: '/platform/backups' },
];

// The top links in rail order — Inbox, My Learning, Directory — consumed by the
// satellites' PlatformSidebar as one ordered list (the dashboard renders its
// own top section). Inbox and Directory are dashboard-owned routes (`path`,
// prefixed with the dashboard URL); My Learning is another module's absolute
// `url`, dropped when REACT_APP_TRAINING_URL is unset.
export const TOP_LINKS = [
  { name: 'Inbox', icon: InboxIcon, path: '/messages' },
  {
    name: 'My Learning',
    icon: AcademicCapIcon,
    url: process.env.REACT_APP_TRAINING_URL ? `${process.env.REACT_APP_TRAINING_URL}/my-learning` : null,
  },
  { name: 'Directory', icon: UsersIcon, path: '/directory' },
  { name: 'Helpdesk', icon: LifebuoyIcon, path: '/helpdesk' },
  { name: 'Email Signature', icon: PencilSquareIcon, path: '/signatures' },
].filter((item) => item.path || item.url);

// The departments, in rail order. Only the spine lives here — key, label, icon,
// and the copy the dashboard's department panel shows. The per-department cards
// (module URLs, groups, headerKeys) stay in the dashboard, which is the only
// app that renders them.
//
// `key` is the contract between apps: the satellites link to
// `${dashboardUrl}/?dept=${key}`, and the server returns the same keys in
// access.departments.
export const DEPARTMENT_SPINE = [
  {
    key: 'it',
    name: 'IT',
    fullName: 'IT (Information Technology)',
    icon: CpuChipIcon,
    description: 'Internal tools and engineering resources.',
  },
  {
    key: 'infosec',
    name: 'Infosec',
    fullName: 'Infosec',
    icon: LockClosedIcon,
    description: 'ISO 27001 compliance evidence and access review history.',
  },
  {
    key: 'hr',
    name: 'HR',
    fullName: 'HR (Human Resources)',
    icon: IdentificationIcon,
    description: 'People data and employee activity reporting.',
  },
  {
    key: 'health-safety',
    name: 'Health & Safety',
    fullName: 'Health & Safety',
    icon: ShieldCheckIcon,
    description: 'Safety, health, environment, and quality resources.',
  },
  {
    key: 'operations',
    name: 'Operations',
    fullName: 'Operations',
    icon: WrenchScrewdriverIcon,
    description: 'Field operations records and reporting.',
  },
  {
    key: 'communications',
    name: 'Communications',
    fullName: 'Communications',
    icon: MegaphoneIcon,
    description: 'Broadcast messages to staff and engineers, with read/acknowledge analytics.',
  },
  {
    key: 'support',
    name: 'Support',
    fullName: 'Support',
    icon: LifebuoyIcon,
    description: 'Live assistance for engineers — reply to help requests from the field.',
  },
  {
    key: 'logistics',
    name: 'Logistics',
    fullName: 'Logistics',
    icon: CubeIcon,
    description: 'Field stock counts and logistics tools.',
  },
  {
    key: 'fleet',
    name: 'Fleet',
    fullName: 'Fleet',
    icon: TruckIcon,
    description: 'Vehicle fleet — out-of-hours vehicle usage requests and approvals.',
  },
  {
    key: 'fsm',
    name: 'FSM',
    fullName: 'Field Service Mobile',
    icon: DevicePhoneMobileIcon,
    description: 'Engineer-facing field portal — stock counts, inspections.',
  },
  {
    key: 'training',
    name: 'Training',
    fullName: 'Training',
    icon: AcademicCapIcon,
    description: 'Build and assign staff training courses.',
  },
];

// Deny-by-default: a department is visible ONLY if the user is a member of its
// configured Entra group. There is no "ungated = open" exception — every
// department must have a group in nano-fibre-tech's permissionGroups.js, or
// nobody sees it at all, including its own admins until they add themselves.
export function filterDepartmentsByAccess(departments, access) {
  if (!access) return [];
  return departments.filter((dept) => access.departments.includes(dept.key));
}

// The server's explicit `fullAdmin` flag is authoritative. It decouples Platform
// visibility from the exact department set — adding a department no longer hides
// Platform for a client whose cached my-access predates that department (which is
// what broke Integrations when Support was added). The department-spine heuristic
// remains as a fallback for any older my-access response that lacks the flag.
//
// UX only — the real gate is server-side on every /api/platform request.
export function isFullAdmin(access) {
  if (!access) return false;
  if (typeof access.fullAdmin === 'boolean') return access.fullAdmin;
  if (!access.departments?.length) return false;
  return DEPARTMENT_SPINE.every((dept) => access.departments.includes(dept.key));
}
